import { DocumentNode, useMutation } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import ActivatePurchaseMutation from './ActivatePurchaseMutation';
import DeactivatePurchaseMutation from './DeactivatePurchaseMutation';
import useConfirmModal from '../../../../common/useConfirmModal';

interface UseTogglePurchaseMutationsProps {
  onActivated?: () => void;
  onDeactivated?: () => void;
  refetchQueries?: DocumentNode[];
}

interface Purchase {
  id?: string | null;
  batch_size: number;
}

const useTogglePurchaseMutations = ({
  onActivated, onDeactivated, refetchQueries,
}: UseTogglePurchaseMutationsProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const [purchase, setPurchase] = useState<Purchase>();

  const [activate] = useMutation(
    ActivatePurchaseMutation,
    {
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        onActivated?.();
        notifier.success(t(`notifications.${data.activatePurchase.ticket ? 'registration_activated' : 'purchase_activated'}`));
      },
    });

  const [deactivate] = useMutation(
    DeactivatePurchaseMutation,
    {
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        onDeactivated?.();
        notifier.success(t(`notifications.${data.deactivatePurchase.ticket ? 'registration_deactivated' : 'purchase_deactivated'}`));
      },
    },
  );

  const [openActivatePurchaseModal] = useConfirmModal({
    title: t('activate_group_registration'),
    body: t('activate_group_registration_description', { count: purchase?.batch_size }),
    onConfirm: () => activate({ variables: { id: purchase.id } }),
  }, [purchase]);

  const activatePurchase = (purchase: Purchase) => {
    if (purchase.batch_size > 1) {
      setPurchase(purchase);
      openActivatePurchaseModal();
    } else {
      activate({ variables: { id: purchase.id } });
    }
  };

  const [openDeactivatePurchaseModal] = useConfirmModal({
    title: t('deactivate_group_registration'),
    body: t('deactivate_group_registration_description', { count: purchase?.batch_size }),
    onConfirm: () => deactivate({ variables: { id: purchase.id } }),
  }, [purchase]);

  const deactivatePurchase = (purchase: Purchase) => {
    if (purchase.batch_size > 1) {
      setPurchase(purchase);
      openDeactivatePurchaseModal();
    } else {
      deactivate({ variables: { id: purchase.id } });
    }
  };

  return { activatePurchase, deactivatePurchase };
};

export default useTogglePurchaseMutations;
